"use client";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { cx } from "../internal/cx.js";
import { usePopover } from "../internal/usePopover.js";
const FOCUSABLE = "input:not([disabled]), button:not([disabled]), [href], select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";
const isElement = (anchor) => typeof anchor.getBoundingClientRect === "function";
/**
 * A rectangle as an anchor: it measures as the rectangle, lives in <body> (or the open dialog), contains nothing and takes no focus
 * — focus goes back to where it was before the panel opened.
 */
function rectAnchor(rect, home) {
    const box = { x: rect.x, y: rect.y, left: rect.x, top: rect.y, width: rect.width, height: rect.height, right: rect.x + rect.width, bottom: rect.y + rect.height };
    return {
        getBoundingClientRect: () => ({ ...box, toJSON: () => box }),
        closest: (selector) => home?.closest(selector) ?? null,
        contains: () => false,
        focus: () => { },
    };
}
/**
 * A small panel next to an element (or a rectangle — selected text, a point), on top of everything (fixed, in a portal): flips up when there is no room below,
 * stays in the viewport, carries a wrapper's `data-theme`. Esc and a pointer-down outside close it; focus moves inside on open
 * and back to the anchor on close.
 */
export function Popover({ anchor, onClose, label, children, role = "dialog", minWidth = 220, flush, className }) {
    const opener = useRef(null);
    const rectKey = anchor && !isElement(anchor) ? `${anchor.x},${anchor.y},${anchor.width},${anchor.height}` : "";
    const virtual = useMemo(() => {
        if (!anchor || isElement(anchor))
            return null;
        opener.current = document.activeElement;
        return rectAnchor(anchor, opener.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- a new object with the same rectangle is the same anchor
    }, [rectKey]);
    const element = anchor && isElement(anchor) ? anchor : virtual;
    const anchorRef = useRef(element);
    const virtualRef = useRef(virtual);
    virtualRef.current = virtual;
    anchorRef.current = element;
    const closeRef = useRef(onClose);
    closeRef.current = onClose;
    const popover = usePopover(anchorRef, { minWidth, height: 300, fit: true, capture: true, onClose: () => closeRef.current() });
    const { open, show, hide, panel } = popover;
    useLayoutEffect(() => { if (element)
        show();
    else
        hide(); }, [element, show, hide]);
    // Second pass once the panel exists: measure its real height for the flip-up decision.
    useLayoutEffect(() => { if (open)
        show(); }, [open, show]);
    useEffect(() => {
        if (!open)
            return;
        const element = panel.current;
        const previous = document.activeElement;
        if (element && !element.contains(document.activeElement))
            (element.querySelector(FOCUSABLE) ?? element).focus();
        return () => {
            const active = document.activeElement;
            if (!active || active === document.body || element?.contains(active))
                (anchorRef.current && anchorRef.current === virtualRef.current ? previous : anchorRef.current ?? previous)?.focus();
        };
    }, [open, panel]);
    if (!element || !open || !popover.position)
        return null;
    return createPortal(_jsx("div", { ref: panel, className: cx("jui-popover", "jui-popover--free", flush && "jui-popover--flush", className), role: role, "aria-label": label, "data-theme": popover.position.theme, "data-density": popover.position.density, "data-up": popover.position.bottom !== undefined || undefined, style: popover.style, tabIndex: -1, children: children }), popover.position.container);
}
